import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import { css, createGlobalStyle } from '@xstyled/styled-components';
import merge from 'lodash.merge';
import { hexToRGB, VARIANTS, hexToRGBA } from '@trybeapp/ui.utilities';
import { th, getFont as getFont$1, getColor } from '@xstyled/system';
import { lighten, darken, normalize } from 'polished';

var getAlerts = function getAlerts(theme) {
  var colors = theme.colors,
    fontSizes = theme.fontSizes,
    radii = theme.radii,
    space = theme.space,
    shadows = theme.shadows,
    weights = theme.weights;
  return {
    "default": {
      'font-size': fontSizes.body2,
      'border-radius': radii.md,
      padding: space.lg,
      'box-shadow': shadows.sm
    },
    title: {
      color: 'inherit',
      'font-size': fontSizes.body1,
      margin: 0,
      'margin-bottom': space.sm,
      'font-weight': weights.bold
    },
    error: {
      'background-color': colors.danger[100],
      color: colors.danger[500]
    },
    warning: {
      'background-color': colors.warning[100],
      color: colors.warning[900]
    },
    info: {
      'background-color': colors.info[100],
      color: colors.info[500]
    },
    success: {
      'background-color': colors.success[200],
      color: colors.light[900]
    }
  };
};

var getButtons = function getButtons(theme) {
  var colors = theme.colors,
    fontSizes = theme.fontSizes,
    weights = theme.weights,
    letterSpacings = theme.letterSpacings,
    radii = theme.radii,
    shadows = theme.shadows,
    space = theme.space;
  var defaults = {
    color: colors.light[700],
    'font-size': fontSizes.button,
    'font-weight': weights.bold,
    'text-transform': 'uppercase',
    'letter-spacing': letterSpacings.sm,
    'background-color': colors.primary[900],
    'border-color': colors.primary[900],
    'border-radius': radii.md,
    'line-height': '100%',
    '&:hover': {
      'box-shadow': shadows.sm
    },
    '&:focus': {
      'box-shadow': "0 0 0 3px rgba(" + hexToRGB(colors.info[500]) + ", 0.5)"
    }
  };
  return {
    primary: defaults,
    secondary: _extends({}, defaults, {
      color: colors.light[700],
      'background-color': colors.secondary[700],
      'border-color': colors.secondary[700]
    }),
    tertiary: _extends({}, defaults, {
      color: colors.nude[700],
      'background-color': colors.nude[200],
      'border-color': colors.nude[200]
    }),
    'primary-warning': _extends({}, defaults, {
      color: colors.light[700],
      'background-color': colors.warning[500],
      'border-color': colors.warning[500]
    }),
    'secondary-warning': _extends({}, defaults, {
      color: colors.warning[500],
      'background-color': colors.light[900],
      'border-color': colors.warning[500]
    }),
    'primary-danger': _extends({}, defaults, {
      color: colors.light[700],
      'background-color': colors.danger[500],
      'border-color': colors.danger[500]
    }),
    'secondary-danger': _extends({}, defaults, {
      color: colors.danger[500],
      'background-color': colors.light[900],
      'border-color': colors.danger[500]
    }),
    'primary-info': _extends({}, defaults, {
      color: colors.light[700],
      'background-color': colors.info[500],
      'border-color': colors.info[500]
    }),
    'secondary-info': _extends({}, defaults, {
      color: colors.info[500],
      'background-color': colors.light[900],
      'border-color': colors.info[500]
    }),
    'primary-success': _extends({}, defaults, {
      color: colors.light[700],
      'background-color': colors.success[500],
      'border-color': colors.success[500]
    }),
    'secondary-success': _extends({}, defaults, {
      color: colors.success[500],
      'background-color': colors.light[900],
      'border-color': colors.success[500]
    }),
    ghost: _extends({}, defaults, {
      color: colors.nude[800],
      'background-color': 'transparent',
      'border-color': 'transparent'
    }),
    hover: {
      primary: {
        'background-color': colors.primary[500],
        'border-color': colors.primary[500]
      },
      secondary: {
        'background-color': colors.secondary[500],
        'border-color': colors.secondary[500]
      },
      tertiary: {
        color: colors.nude[800],
        'background-color': colors.nude[400],
        'border-color': colors.nude[400]
      },
      'primary-warning': {
        'background-color': colors.warning[200],
        'border-color': colors.warning[200]
      },
      'secondary-warning': {
        'background-color': colors.warning[100],
        'border-color': colors.warning[200]
      },
      'primary-danger': {
        'background-color': colors.danger[200],
        'border-color': colors.danger[200]
      },
      'secondary-danger': {
        'background-color': colors.danger[100],
        'border-color': colors.danger[200]
      },
      'primary-info': {
        'background-color': colors.info[200],
        'border-color': colors.info[200]
      },
      'secondary-info': {
        'background-color': colors.info[100],
        'border-color': colors.info[200]
      },
      'primary-success': {
        'background-color': colors.success[200],
        'border-color': colors.success[200]
      },
      'secondary-success': {
        'background-color': colors.success[100],
        'border-color': colors.success[200]
      },
      ghost: {
        'background-color': colors.light[700],
        'border-color': 'transparent',
        'box-shadow': 'none'
      }
    },
    focus: {
      primary: {
        'background-color': colors.primary[900],
        'border-color': colors.primary[900]
      },
      secondary: {
        'background-color': colors.secondary[700],
        'border-color': colors.secondary[700]
      },
      tertiary: {
        color: colors.nude[800],
        'background-color': colors.nude[400],
        'border-color': colors.nude[400]
      },
      'primary-warning': {
        'background-color': colors.warning[700],
        'border-color': colors.warning[700]
      },
      'secondary-warning': {
        'background-color': colors.light[900],
        'border-color': colors.warning[700]
      },
      'primary-danger': {
        'background-color': colors.danger[700],
        'border-color': colors.danger[700]
      },
      'secondary-danger': {
        'background-color': colors.light[900],
        'border-color': colors.danger[700]
      },
      'primary-info': {
        'background-color': colors.info[700],
        'border-color': colors.info[700]
      },
      'secondary-info': {
        'background-color': colors.light[900],
        'border-color': colors.info[700]
      },
      'primary-success': {
        'background-color': colors.success[700],
        'border-color': colors.success[700]
      },
      'secondary-success': {
        'background-color': colors.light[900],
        'border-color': colors.success[700]
      },
      ghost: {
        'background-color': colors.light[700],
        'border-color': 'transparent',
        'box-shadow': 'none'
      }
    },
    disabled: _extends({}, defaults, {
      color: colors.nude[600],
      'background-color': colors.nude[100],
      'border-color': colors.nude[100]
    }),
    sizes: {
      xs: {
        height: '1.25rem',
        padding: "0 " + space.sm
      },
      sm: {
        height: '1.75rem',
        padding: "0 " + space.sm
      },
      md: {
        height: '2.25rem',
        padding: "0 " + space.md
      },
      lg: {
        height: '2.5rem',
        padding: "0 " + space.lg
      },
      xl: {
        height: '4.375rem',
        padding: "0 " + space.lg
      }
    }
  };
};

var getCards = function getCards(theme) {
  var radii = theme.radii,
    colors = theme.colors;
  return {
    "default": {
      'background-color': colors.light[900],
      'border-color': colors.light[500],
      overflow: 'hidden'
    },
    cover: {
      'border-top-left-radius': radii.sm,
      'border-top-right-radius': radii.sm
    }
  };
};

// To allow for line-height of text in label
var LINE_HEIGHT_ADJUSTMENTS = '0.15rem';
var getFields = function getFields(theme) {
  var colors = theme.colors,
    fontSizes = theme.fontSizes,
    weights = theme.weights,
    radii = theme.radii,
    shadows = theme.shadows,
    space = theme.space,
    toRem = theme.toRem;
  var defaults = {
    color: colors.nude[800],
    'font-size': fontSizes.body3,
    'line-height': toRem(16),
    'font-weight': weights.regular,
    'background-color': colors.light[900],
    'border-color': colors.nude[200],
    'border-width': '1px',
    'border-style': 'solid',
    'border-radius': radii.md,
    outline: 'none'
  };
  var checked = {
    'background-color': colors.primary[500],
    'border-color': colors.primary[500],
    color: colors.light[900],
    '&::after': {
      opacity: 1
    }
  };
  return {
    "default": defaults,
    disabled: {
      'background-color': colors.light[500],
      color: colors.nude[600],
      cursor: 'not-allowed'
    },
    focused: {
      'border-color': colors.primary[500]
    },
    sizes: {
      sm: {
        height: '2rem',
        padding: space.sm + " " + space.md
      },
      md: {
        height: '2.25rem',
        padding: space.sm + " " + space.md
      },
      lg: {
        height: '2.5rem',
        padding: space.md
      }
    },
    placeholder: {
      color: colors.nude[600]
    },
    hint: {
      color: colors.nude[600],
      'font-size': fontSizes.body3,
      'font-weight': weights.regular
    },
    label: {
      color: colors.nude[900],
      'font-size': fontSizes.body3,
      'font-weight': weights.regular,
      'min-height': '1.4em'
    },
    checkablelabel: {
      "default": {
        'font-weight': weights.regular
      },
      checked: {
        color: colors.dark[200],
        '-webkit-text-stroke': '0.04em'
      }
    },
    checkbox: {
      "default": {
        width: toRem(15),
        height: toRem(15),
        'flex-shrink': 0,
        'margin-top': LINE_HEIGHT_ADJUSTMENTS,
        'border-color': colors.nude[400]
      },
      checked: checked
    },
    radio: {
      "default": {
        width: toRem(18),
        height: toRem(18),
        'border-radius': '50%'
      },
      checked: _extends({}, checked, {
        'border-radius': '50%'
      })
    },
    fileupload: {
      'border-style': 'dashed',
      height: toRem(300)
    },
    mde: {
      icons: {
        'font-size': fontSizes.body3,
        color: colors.secondary[500],
        '&.active': {
          color: colors.primary[200],
          'border-color': colors.nude[300]
        },
        '&:hover': {
          color: colors.primary[200],
          'border-color': colors.nude[300]
        }
      }
    },
    radiotabs: {
      "default": _extends({}, defaults),
      checked: {
        color: colors.dark[200],
        'background-color': colors.light[700],
        'border-color': colors.primary[500],
        '&:hover': {
          'box-shadow': 'none'
        }
      },
      hover: {
        'background-color': colors.light[700],
        'box-shadow': shadows.sm
      }
    },
    select: {
      "default": {
        'font-size': fontSizes.body3,
        'max-height': toRem(155)
      },
      existing: {
        color: colors.nude[500],
        cursor: 'not-allowed'
      },
      highlighted: {
        'background-color': colors.light[500],
        cursor: 'default'
      },
      selected: {
        color: colors.dark[200],
        'font-weight': weights.bold,
        'background-color': colors.primary[100]
      }
    },
    textarea: {
      'min-height': '8rem'
    },
    toggles: {
      "default": {
        width: toRem(22),
        'background-color': colors.nude[200],
        'border-color': colors.nude[200],
        'margin-top': "-" + LINE_HEIGHT_ADJUSTMENTS,
        '&::after': _extends({}, defaults, {
          'background-color': colors.light[700],
          'border-color': colors.nude[400]
        })
      },
      checked: {
        'background-color': colors.primary[500],
        'border-color': colors.primary[500],
        '&::after': {
          'border-color': colors.primary[500]
        }
      },
      disabled: {
        'border-color': colors.light[200],
        'background-color': colors.light[200],
        '&::after': {
          'border-color': colors.nude[200]
        }
      },
      checkedDisabled: {
        'border-color': colors.secondary[200],
        'background-color': colors.secondary[200],
        '&::after': {
          'border-color': colors.secondary[200]
        }
      }
    },
    fieldset: {
      'border-width': '0'
    }
  };
};

var getIcons = function getIcons(_ref) {
  var toRem = _ref.toRem;
  return {
    sizes: {
      xs: toRem(10),
      sm: toRem(12),
      md: toRem(15),
      lg: toRem(20),
      xl: toRem(27.574)
    }
  };
};

var getLinks = function getLinks(theme) {
  var colors = theme.colors,
    underline = theme.underline;
  return {
    "default": {
      color: colors.dark[500]
    },
    primary: underline,
    secondary: {}
  };
};

var getModals = function getModals(_ref) {
  var colors = _ref.colors,
    radii = _ref.radii,
    modalWidths = _ref.modalWidths,
    toRem = _ref.toRem,
    borderWidths = _ref.borderWidths;
  return {
    backdrop: {
      'background-color': colors.overlay,
      opacity: 0.7,
      'z-index': '10'
    },
    "default": {
      'z-index': '10'
    },
    gutter: toRem(32),
    sizes: {
      sm: {
        width: modalWidths.sm
      },
      md: {
        width: modalWidths.md
      },
      lg: {
        width: modalWidths.lg
      },
      auto: {}
    },
    cover: {
      'border-top-left-radius': radii.sm,
      'border-top-right-radius': radii.sm
    },
    header: {
      'border-bottom-color': colors.light[500],
      'border-bottom-style': 'solid',
      'border-bottom-width': borderWidths.sm
    },
    footer: {
      'border-top-color': colors.light[500],
      'border-top-style': 'solid',
      'border-top-width': borderWidths.sm
    }
  };
};

var getPaginations = function getPaginations(_ref) {
  var colors = _ref.colors,
    fontSizes = _ref.fontSizes,
    weights = _ref.weights,
    space = _ref.space,
    toRem = _ref.toRem,
    radii = _ref.radii;
  return {
    "default": {
      color: colors.nude[800],
      'background-color': colors.light[900],
      'border-color': 'transparent',
      'border-width': '1px',
      'border-style': 'solid',
      width: toRem(30),
      height: toRem(30),
      '&:hover': {
        'background-color': colors.nude[200]
      }
    },
    active: {
      color: colors.light[900],
      'background-color': colors.primary[500],
      'border-color': colors.primary[500],
      '&:hover, &:focus': {
        color: colors.light[900],
        'background-color': colors.primary[500]
      }
    },
    number: {
      'border-color': colors.nude[200],
      'font-size': fontSizes.body3
    },
    dots: {
      color: colors.nude[600],
      'font-size': fontSizes.body3,
      'border-color': colors.nude[200],
      '&:hover, &:focus': {
        border: 'none'
      }
    },
    link: {
      width: 'auto',
      'font-size': fontSizes.body3,
      'padding-left': space.md,
      'padding-right': space.md,
      'border-color': colors.nude[200]
    }
  };
};

var getSpinners = function getSpinners(theme) {
  return {
    "default": {
      trackColor: theme.colors.nude[600],
      headColor: theme.colors.nude[600]
    },
    sizes: {
      xs: 16,
      sm: 22,
      md: 35,
      lg: 60,
      xl: 100
    }
  };
};

var getTables = function getTables(theme) {
  var borderWidths = theme.borderWidths,
    colors = theme.colors,
    fontSizes = theme.fontSizes,
    letterSpacings = theme.letterSpacings;
  return {
    th: {
      'font-size': fontSizes.body4,
      color: colors.nude[900],
      'text-align': 'left',
      'text-transform': 'uppercase',
      'letter-spacing': letterSpacings.md
    },
    td: {
      'border-top-color': colors.light[500],
      'border-top-width': borderWidths.sm,
      'border-top-style': 'solid',
      'text-align': 'left'
    },
    tr: {
      "default": {
        'font-size': fontSizes.body3,
        color: colors.nude[800],
        'word-break': 'normal'
      },
      error: {
        'background-color': colors.danger[100]
      },
      warning: {
        'background-color': colors.warning[100]
      },
      info: {
        'background-color': colors.info[100]
      },
      success: {
        'background-color': colors.success[100]
      },
      clickable: {
        cursor: 'pointer'
      }
    }
  };
};

var TAG_VARIANTS = /*#__PURE__*/_extends({
  blue: 'colors.sub.blue',
  dark: 'colors.dark.500',
  "default": 'colors.nude.100',
  green: 'colors.sub.green',
  orange: 'colors.sub.orange',
  pink: 'colors.sub.pink',
  primary: 'colors.primary.500',
  purple: 'colors.sub.purple',
  red: 'colors.sub.red',
  secondary: 'colors.secondary.500',
  turquoize: 'colors.sub.turquoize',
  yellow: 'colors.sub.yellow'
}, VARIANTS);
var getVariantsTheme = function getVariantsTheme(theme) {
  return Object.entries(TAG_VARIANTS).reduce(function (acc, _ref) {
    var variant = _ref[0],
      color = _ref[1];
    acc[variant] = {
      'background-color': th(color)({
        theme: theme
      })
    };
    return acc;
  }, {});
};
var getTags = function getTags(theme) {
  var colors = theme.colors,
    fontSizes = theme.fontSizes,
    fontWeights = theme.fontWeights,
    space = theme.space,
    toRem = theme.toRem;
  var sizes = {
    xs: toRem(18),
    sm: toRem(19),
    md: toRem(21),
    lg: toRem(27)
  };
  return {
    "default": {
      'font-size': fontSizes.meta2,
      'font-weight': fontWeights.regular,
      color: colors.light[900]
    },
    variants: _extends({}, getVariantsTheme(theme), {
      "default": {
        'background-color': colors.light[500],
        color: colors.nude[800]
      }
    }),
    sizes: {
      xs: {
        padding: "0 " + space.xs,
        height: sizes.xs
      },
      sm: {
        padding: "0 " + space.sm,
        height: sizes.sm
      },
      md: {
        padding: "0 " + space.md,
        height: sizes.md
      },
      lg: {
        padding: "0 " + space.lg,
        height: sizes.lg,
        'font-size': fontSizes.meta1
      }
    },
    shape: {
      sm: {
        width: sizes.sm,
        height: sizes.sm
      },
      md: {
        width: sizes.md,
        height: sizes.md
      },
      lg: {
        width: sizes.lg,
        height: sizes.lg
      }
    }
  };
};

var getFontSizes = function getFontSizes(unit, theme) {
  var toEm = theme.toEm,
    toRem = theme.toRem;
  var convert = unit === 'em' ? toEm : toRem;
  return {
    h1: convert(36),
    h2: convert(28),
    h3: convert(22),
    h4: convert(18),
    body1: convert(17),
    body2: convert(15),
    body3: convert(13),
    body4: convert(11),
    button: convert(11),
    subtitle1: convert(13),
    subtitle2: convert(11),
    meta1: convert(13),
    meta2: convert(11)
  };
};
var getLineHeights = function getLineHeights(unit, theme) {
  return {
    html: '1.6em',
    h1: '1.6em',
    h2: '1.6em',
    h3: '1.6em',
    h4: '1.6em',
    body1: '1.6em',
    body2: '1.6em',
    body3: '1.6em',
    body4: '1.6em',
    button: '1.6em',
    subtitle1: '1.6em',
    subtitle2: '1.6em',
    meta1: '1.6em',
    meta2: '1.6em'
  };
};
var getFontWeights = function getFontWeights(theme) {
  return {
    html: theme.weights.regular,
    h1: theme.weights.regular,
    h2: theme.weights.regular,
    h3: theme.weights.bold,
    h4: theme.weights.regular,
    body1: theme.weights.regular,
    body2: theme.weights.regular,
    body3: theme.weights.regular,
    body4: theme.weights.regular,
    button: theme.weights.regular,
    subtitle1: theme.weights.regular,
    subtitle2: theme.weights.regular,
    meta1: theme.weights.regular,
    meta2: theme.weights.regular
  };
};
var getTexts = function getTexts(theme) {
  var fontSizes = theme.fontSizes,
    fontWeights = theme.fontWeights,
    lineHeights = theme.lineHeights;
  return Object.keys(fontSizes).reduce(function (acc, key) {
    var _extends2;
    return _extends({}, acc, (_extends2 = {}, _extends2[key] = {
      'font-weight': fontWeights[key],
      'font-size': fontSizes[key],
      'line-height': lineHeights[key] || lineHeights.body1
    }, _extends2));
  }, {});
};

var getToasts = function getToasts(theme) {
  var colors = theme.colors,
    fontWeights = theme.fontWeights,
    radii = theme.radii,
    space = theme.space,
    shadows = theme.shadows;
  return {
    "default": {
      'padding-left': space.sm,
      'padding-right': space.sm
    },
    top: {
      'padding-top': space.lg
    },
    bottom: {
      'padding-bottom': space.lg
    },
    growls: {
      "default": _extends({}, getTexts(theme).body3, {
        'box-shadow': shadows.md,
        'border-radius': radii.md,
        color: colors.light[900]
      }),
      title: _extends({}, getTexts(theme).body2, {
        'font-weight': fontWeights.bold
      })
    }
  };
};

// To allow for line-height of text in label
var LINE_HEIGHT_ADJUSTMENTS$1 = '0.15rem';
var getToggles = function getToggles(theme) {
  var borderWidths = theme.borderWidths,
    colors = theme.colors,
    focus = theme.focus,
    toRem = theme.toRem;
  return {
    item: {
      "default": {
        width: toRem(34),
        height: toRem(18),
        borderRadius: toRem(9),
        backgroundColor: colors.light[800],
        borderColor: colors.nude[200],
        borderWidth: borderWidths.sm,
        borderStyle: 'solid',
        marginTop: LINE_HEIGHT_ADJUSTMENTS$1,
        '&:focus': _extends({
          borderColor: colors.primary[500]
        }, focus())
      },
      checked: {
        backgroundColor: colors.primary[500],
        borderColor: colors.primary[500]
      },
      disabled: {
        borderColor: colors.nude[700],
        backgroundColor: colors.nude[500]
      }
    },
    after: {
      "default": {
        width: 14,
        height: 14,
        backgroundColor: colors.light[900],
        borderColor: colors.light[500],
        borderWidth: borderWidths.sm,
        borderStyle: 'solid',
        borderRadius: '50%'
      },
      checked: {
        backgroundColor: colors.light[900],
        borderColor: colors.light[900]
      },
      disabled: {
        borderColor: colors.nude[700],
        backgroundColor: colors.nude[700]
      }
    }
  };
};

var palette = {
  darkpurple: '#1C203C',
  brightpink: '#ff295d',
  jade: '#00A772',
  mountainmeadow: '#23C28F',
  spanishviridian: '#00875C',
  //greens
  deepteal: '#002E32',
  cyprus: '#163D40',
  aquadeep: '#134B45',
  eden: '#2D5351',
  cadet: '#567672',
  seanymph: '#849C96',
  edward: '#9DB0AA',
  opal: '#AABEB9',
  junglemist: '#B9CCC6',
  nebula: '#CCDBD7',
  gin: '#E3F0EC',
  //nudes
  concord: '#7F7C7A',
  zorba: '#97938F',
  silverchalice: '#AFABA7',
  silversand: '#BFBDB9',
  quillgray: '#D7D5D1',
  gainsboro: '#E1DFDB',
  cararra: '#EEECE8',
  isabelline: '#F2F0ED',
  pampas: '#F6F4F2',
  vistawhite: '#FBF9F7',
  soapstone: '#FFFDFB',
  // states : infos, warning & danger
  bluedefrance: '#3790F0',
  bluejeans: '#5EAAFC',
  bronze: '#CA8E2A',
  anzac: '#DDA343',
  casablanca: '#F1B85B',
  sweetbrown: '#A53626',
  carmen: '#BF4C3B',
  flamingo: '#E16654',
  //colorpicker
  blue: '#559AF1',
  coral: '#EA724C',
  green: '#4AB519',
  pink: '#F0AABF',
  purple: '#965FE6',
  red: '#D04232',
  turquoize: '#3FD1C1',
  yellow: '#F4CF70',
  // pastel
  mint: '#DFF5EB',
  aliceblue: '#DFEAFF',
  floralwhite: '#FFF2DC',
  rosewhite: '#FFE7E7'
};
var colors = {
  primary: {
    100: /*#__PURE__*/lighten(0.7, palette.darkpurple),
    200: /*#__PURE__*/lighten(0.5, palette.darkpurple),
    500: /*#__PURE__*/lighten(0.3, palette.darkpurple),
    700: /*#__PURE__*/lighten(0.2, palette.darkpurple),
    900: palette.darkpurple
  },
  secondary: {
    100: '#ffeaef',
    200: '#ffbfce',
    500: '#ff7f9e',
    700: '#ff295d',
    900: '#b31d41'
  },
  success: {
    100: '#C5F3C9',
    200: '#52B45A',
    500: '#2F9237',
    700: /*#__PURE__*/darken(0.1, '#2F9237'),
    900: /*#__PURE__*/darken(0.2, '#2F9237')
  },
  danger: {
    100: palette.rosewhite,
    200: palette.flamingo,
    500: palette.carmen,
    700: palette.sweetbrown,
    900: /*#__PURE__*/darken(0.1, palette.sweetbrown)
  },
  warning: {
    100: palette.floralwhite,
    200: palette.casablanca,
    500: palette.anzac,
    700: palette.bronze,
    900: /*#__PURE__*/darken(0.1, palette.bronze)
  },
  info: {
    100: palette.aliceblue,
    200: palette.bluejeans,
    500: palette.bluedefrance,
    700: /*#__PURE__*/darken(0.1, palette.bluedefrance),
    900: /*#__PURE__*/darken(0.2, palette.bluedefrance)
  },
  light: {
    200: '#e7e7e7',
    500: '#f1f1f1',
    700: '#fafafa',
    800: '#fcfcfc',
    900: '#FFFFFF'
  },
  dark: {
    200: '#2b2963',
    500: '#1f1e47',
    700: '#12122b',
    900: '#000'
  },
  nude: {
    100: '#dbdbdb',
    200: '#c9caca',
    300: '#b7b8b8',
    400: '#a6a6a6',
    500: '#949494',
    600: '#828282',
    700: '#707171',
    800: '#5e5f5f',
    900: '#4c4d4d'
  },
  sub: {
    blue: '#559AF1',
    green: '#4AB519',
    orange: '#EA724C',
    pink: '#F0AABF',
    purple: '#965FE6',
    red: '#D04232',
    turquoize: '#3FD1C1',
    yellow: '#F4CF70'
  },
  overlay: /*#__PURE__*/hexToRGBA('#000000', 0.7)
};

var fontFaces = {
  welcomeweb: [{
    url: 'https://cdn.welcometothejungle.co/common/assets/fonts/welcomeweb-regular',
    weight: '400',
    extensions: ['woff2', 'woff', 'ttf']
  }, {
    url: 'https://cdn.welcometothejungle.co/common/assets/fonts/welcomeweb-medium',
    weight: '500',
    extensions: ['woff2', 'woff', 'ttf']
  }, {
    url: 'https://cdn.welcometothejungle.co/common/assets/fonts/welcomeweb-bold',
    weight: '600',
    extensions: ['woff2', 'woff', 'ttf']
  }, {
    url: 'https://cdn.welcometothejungle.co/common/assets/fonts/welcomeweb-black',
    weight: '700',
    extensions: ['woff2', 'woff', 'ttf']
  }, {
    url: 'https://cdn.welcometothejungle.co/common/assets/fonts/welcomeweb-regularitalic',
    weight: '400',
    style: 'italic',
    extensions: ['woff2', 'woff', 'ttf']
  }, {
    url: 'https://cdn.welcometothejungle.co/common/assets/fonts/welcomeweb-mediumitalic',
    weight: '500',
    style: 'italic',
    extensions: ['woff2', 'woff', 'ttf']
  }, {
    url: 'https://cdn.welcometothejungle.co/common/assets/fonts/welcomeweb-bolditalic',
    weight: '600',
    style: 'italic',
    extensions: ['woff2', 'woff', 'ttf']
  }, {
    url: 'https://cdn.welcometothejungle.co/common/assets/fonts/welcomeweb-blackitalic',
    weight: '700',
    style: 'italic',
    extensions: ['woff2', 'woff', 'ttf']
  }]
};

var webFonts = {
  google: {
    families: ['Karla']
  }
};

var transitionCurves = {
  primary: 'ease',
  secondary: 'linear',
  tertiary: 'cubic-bezier(0.41, 0.094, 0.54, 0.07)'
};
var transitions = {
  slow: "500ms " + transitionCurves.tertiary,
  medium: "300ms " + transitionCurves.primary,
  fast: "100ms " + transitionCurves.secondary
};

var _templateObject;
var _excluded = ["defaultFontFamily", "headingFontFamily", "defaultFontSize", "colors"];
var DEFAULT_FONT_SIZE = 14;
var DEFAULT_FONT_FAMILY = 'Karla';
var HEADING_FONT_FAMILY = 'Karla';
var getBaseTheme = function getBaseTheme(options) {
  if (options === void 0) {
    options = {};
  }
  var _options = options,
    _options$defaultFontF = _options.defaultFontFamily,
    defaultFontFamily = _options$defaultFontF === void 0 ? DEFAULT_FONT_FAMILY : _options$defaultFontF,
    _options$headingFontF = _options.headingFontFamily,
    headingFontFamily = _options$headingFontF === void 0 ? HEADING_FONT_FAMILY : _options$headingFontF,
    _options$defaultFontS = _options.defaultFontSize,
    defaultFontSize = _options$defaultFontS === void 0 ? DEFAULT_FONT_SIZE : _options$defaultFontS,
    _options$colors = _options.colors,
    themeColors = _options$colors === void 0 ? {} : _options$colors,
    rest = _objectWithoutPropertiesLoose(_options, _excluded);
  var theme = {};
  theme.defaultFontSize = defaultFontSize;
  theme.toEm = function (px) {
    return px / theme.defaultFontSize + "em";
  };
  theme.toRem = function (px) {
    return px / theme.defaultFontSize + "rem";
  };
  theme.colors = _extends({}, colors, themeColors);
  theme.transitions = transitions;
  theme.transitionCurves = transitionCurves;

  // fonts
  theme.fontFaces = fontFaces;
  theme.webFonts = webFonts;
  theme.fontSizes = getFontSizes('rem', theme);
  theme.lineHeights = getLineHeights('em', theme);
  theme.weights = {
    regular: '400',
    medium: '500',
    bold: '600',
    black: '700'
  };
  theme.fontWeights = getFontWeights(theme);
  theme.letterSpacings = {
    sm: theme.toRem(1),
    md: theme.toRem(2),
    lg: theme.toRem(3)
  };
  theme.fonts = {
    texts: defaultFontFamily,
    headings: headingFontFamily
  };
  theme.breakpoints = [0, 480, 736, 980, 1280];
  theme.breakpoints.xs = theme.breakpoints[0];
  theme.breakpoints.sm = theme.breakpoints[1];
  theme.breakpoints.md = theme.breakpoints[2];
  theme.breakpoints.lg = theme.breakpoints[3];
  theme.breakpoints.xl = theme.breakpoints[4];
  theme.space = [theme.toRem(0), theme.toRem(6), theme.toRem(8), theme.toRem(10), theme.toRem(12), theme.toRem(15), theme.toRem(24), theme.toRem(30)];
  theme.space.xs = theme.space[0];
  theme.space.sm = theme.space[1];
  theme.space.md = theme.space[2];
  theme.space.lg = theme.space[3];
  theme.space.xl = theme.space[4];
  theme.space.xxl = theme.space[5];
  theme.modalWidths = {
    sm: 400,
    md: 550,
    lg: 680
  };
  theme.radii = {
    sm: '4px',
    md: '6px',
    lg: '10px'
  };
  theme.shadows = {
    sm: '1px 2px 4px 0 rgba(0,0,0,0.05)',
    md: '3px 4px 10px 0 rgba(0,0,0,0.07)',
    'inset-sm-primary': "inset 0 -3px 0 " + theme.colors.primary[100]
  };
  theme.focus = function (color) {
    if (color === void 0) {
      color = theme.colors.primary[500];
    }
    return {
      boxShadow: "0 0 0 3px " + hexToRGBA(color, 0.5)
    };
  };
  theme.borderWidths = {
    sm: '1px',
    md: '2px',
    lg: '3px',
    xl: '4px'
  };
  theme.underline = {
    'border-bottom-style': 'solid',
    'border-bottom-width': theme.borderWidths.sm
  };
  theme = merge({}, theme, rest);

  // CSS blocks
  // These attributes depend on colors and fontSizes and must come last
  theme.underline['border-bottom-color'] = theme.colors.primary[500];
  theme.alerts = getAlerts(theme);
  theme.buttons = getButtons(theme);
  theme.cards = getCards(theme);
  theme.fields = getFields(theme);
  theme.icons = getIcons(theme);
  theme.links = getLinks(theme);
  theme.modals = getModals(theme);
  theme.paginations = getPaginations(theme);
  theme.spinners = getSpinners(theme);
  theme.tables = getTables(theme);
  theme.tags = getTags(theme);
  theme.texts = getTexts(theme);
  theme.toasts = getToasts(theme);
  theme.toggles = getToggles(theme);
  theme.reset = css(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n    margin: 0px;\n    padding: 0px;\n    border: 0px;\n    font-size: 100%;\n    font: inherit;\n    vertical-align: baseline;\n    box-sizing: border-box;\n    -webkit-font-smoothing: antialiased;\n    -moz-osx-font-smoothing: grayscale;\n\n    &:focus {\n      outline: none;\n    }\n  "])));
  return theme;
};
var createTheme = function createTheme(options) {
  var base = getBaseTheme(options);
  var theme = merge({}, base, options);
  return theme;
};

var _templateObject$1;
var underlineStyles = /*#__PURE__*/css(_templateObject$1 || (_templateObject$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border-bottom-width: md;\n"])));
var trybeTheme = {
  colors: colors,
  palette: palette,
  underline: underlineStyles,
  fields: {
    "default": {
      'background-color': colors.light[700]
    },
    focused: {
      'background-color': colors.light[900]
    }
  },
  tags: {
    variants: {
      "default": {
        'background-color': colors.nude[200]
      }
    }
  }
};

var _templateObject$2, _templateObject2, _templateObject3;
function getFormat(extension) {
  return extension === 'ttf' ? 'truetype' : extension;
}
function getUrl(url, extension) {
  return "url('" + url + "." + extension + "') format('" + getFormat(extension) + "')";
}
function getSrc(descriptor) {
  return descriptor.extensions.map(function (extension) {
    return getUrl(descriptor.url, extension);
  }).join(', ');
}
function getFont(descriptor) {
  return css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteralLoose(["\n    @font-face {\n      font-family: ", ";\n      src: ", ";\n      font-display: fallback;\n      ", "\n      ", "\n    }\n  "])), descriptor.name, getSrc(descriptor), descriptor.weight && css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n          font-weight: ", ";\n        "])), descriptor.weight), descriptor.style && css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n          font-style: ", ";\n        "])), descriptor.style));
}
var fonts = function fonts() {
  return function (_ref) {
    var theme = _ref.theme;
    if (!theme || !theme.fontFaces) return null;
    return Object.entries(theme.fontFaces)
    // Ignore anything else than array
    .filter(function (_ref2) {
      var variations = _ref2[1];
      return Array.isArray(variations);
    }).map(function (_ref3) {
      var name = _ref3[0],
        variations = _ref3[1];
      return variations.map(function (variation) {
        return getFont(_extends({
          name: name
        }, variation));
      });
    });
  };
};
var webFonts$1 = function webFonts() {
  return function (_ref4) {
    var theme = _ref4.theme;
    if (!theme || !theme.webFonts) return null;
    if (typeof window !== 'undefined') {
      var WebFont = require('webfontloader');
      WebFont.load(theme.webFonts);
    }
  };
};

var _templateObject$3;
var resetStyles = /*#__PURE__*/css(_templateObject$3 || (_templateObject$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  html,\n  body,\n  div,\n  span,\n  applet,\n  object,\n  iframe,\n  h1,\n  h2,\n  h3,\n  h4,\n  h5,\n  h6,\n  p,\n  blockquote,\n  pre,\n  a,\n  abbr,\n  acronym,\n  address,\n  big,\n  cite,\n  code,\n  del,\n  dfn,\n  em,\n  img,\n  ins,\n  kbd,\n  q,\n  s,\n  samp,\n  small,\n  strike,\n  strong,\n  sub,\n  sup,\n  tt,\n  var,\n  b,\n  u,\n  i,\n  center,\n  dl,\n  dt,\n  dd,\n  ol,\n  ul,\n  li,\n  fieldset,\n  form,\n  label,\n  legend,\n  table,\n  caption,\n  tbody,\n  tfoot,\n  thead,\n  tr,\n  th,\n  td,\n  article,\n  aside,\n  canvas,\n  details,\n  embed,\n  figure,\n  figcaption,\n  footer,\n  header,\n  hgroup,\n  menu,\n  nav,\n  output,\n  ruby,\n  section,\n  summary,\n  time,\n  mark,\n  audio,\n  video {\n    margin: 0;\n    padding: 0;\n    border: 0;\n  }\n  article,\n  aside,\n  details,\n  figcaption,\n  figure,\n  footer,\n  header,\n  hgroup,\n  menu,\n  nav,\n  section {\n    display: block;\n  }\n  ol,\n  ul {\n    list-style: none;\n  }\n  blockquote,\n  q {\n    quotes: none;\n  }\n  blockquote::before,\n  blockquote::after,\n  q::before,\n  q::after {\n    content: '';\n    content: none;\n  }\n  table {\n    border-collapse: collapse;\n    border-spacing: 0;\n  }\n  a {\n    text-decoration: none;\n  }\n  img {\n    overflow: hidden;\n  }\n  input {\n    -webkit-appearance: none;\n    -moz-appearance: none;\n    appearance: none;\n  }\n  input::-webkit-search-cancel-button {\n    display: none;\n  }\n  :focus {\n    outline: none;\n  }\n  *,\n  *::before,\n  *::after {\n    box-sizing: border-box;\n    -webkit-font-smoothing: antialiased;\n    -moz-osx-font-smoothing: grayscale;\n  }\n  html {\n    height: 100%;\n    box-sizing: border-box;\n  }\n  body {\n    min-height: 100%;\n  }\n"])));

var _templateObject$4, _templateObject2$1, _templateObject3$1, _templateObject4;
function getFontFamilies() {
  for (var _len = arguments.length, fonts = new Array(_len), _key = 0; _key < _len; _key++) {
    fonts[_key] = arguments[_key];
  }
  return fonts.filter(Boolean).join(', ');
}
function baseFonts(props) {
  var texts = getFont$1('texts')(props);
  var headings = getFont$1('headings')(props);
  var sansSerif = 'sans-serif';
  return css(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteralLoose(["\n    body,\n    button,\n    input,\n    select,\n    textarea {\n      font-family: ", ";\n      -webkit-font-smoothing: antialiased;\n    }\n\n    h1,\n    h2,\n    h3,\n    h4,\n    h5,\n    h6 {\n      font-family: ", ";\n    }\n  "])), getFontFamilies(texts, sansSerif), getFontFamilies(headings, texts, sansSerif));
}
function baseSelection(props) {
  var selectionBgColor = getColor('primary.500')(props) || null;
  var selectionColor = getColor('light.900')(props) || null;
  return css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteralLoose(["\n    ::selection {\n      ", ";\n      ", ";\n    }\n  "])), selectionBgColor && "background-color: " + selectionBgColor, selectionColor && "color: " + selectionColor);
}
var baseBoxSizing = /*#__PURE__*/css(_templateObject3$1 || (_templateObject3$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  html {\n    box-sizing: border-box;\n    -ms-overflow-style: scrollbar;\n  }\n\n  * {\n    &,\n    &::before,\n    &::after {\n      box-sizing: border-box;\n    }\n  }\n"])));
var GlobalStyle = /*#__PURE__*/createGlobalStyle(function (_ref) {
  var _ref$useReset = _ref.useReset,
    useReset = _ref$useReset === void 0 ? false : _ref$useReset;
  return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteralLoose(["\n    ", ";\n    ", ";\n    ", ";\n    ", ";\n    ", ";\n    ", ";\n  "])), normalize(), useReset ? resetStyles : baseBoxSizing, fonts(), webFonts$1(), baseFonts, baseSelection);
});

// Themes

export { GlobalStyle, createTheme, getBaseTheme, resetStyles, trybeTheme };
